import React, { useEffect, useState, useRef } from 'react';
import crm, { STAGES, PRIORITIES, TASK_TYPES, crmUser } from './api';
import { buildCalendarUrl, isMeeting, GCAL_ACCOUNT } from './gcal';

// timeline visuals
const GC = { engagement: '#0891b2', lead: '#1a8046', stage: '#7c3aed', note: '#64748b', task: '#d97706' };
const TICON = { qr_scan: '📱', page_view: '👁️', product_view: '🔍', brochure: '📄', video: '▶️', whatsapp_click: '💬', phone_click: '📞', email_click: '✉️', save_contact: '💾', enquiry: '✅', created: '✨', status: '🔀', note: '📝', task: '📌', task_done: '✔️' };
const fmtWhen = (at) => { if (!at) return ''; const d = new Date(at); return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); };

// Guided sales path (linear). "lost" sits off-path as a close action.
const PATH = ['new', 'contacted', 'qualified', 'demo', 'proposal', 'negotiation', 'won'];
const BACK_REASONS = ['Prospect went quiet', 'Not ready yet', 'Budget on hold', 'Needs re-qualification', 'Moved by mistake'];
const stageMeta = (k) => STAGES.find((s) => s.key === k) || { key: k, label: k, color: '#94a3b8' };

export default function LeadDetail({ id, onClose, onChanged }) {
  const [lead, setLead] = useState(null);
  const [edit, setEdit] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [nt, setNt] = useState('');
  const [nd, setNd] = useState('');
  const [ntype, setNtype] = useState('call');
  const [ntime, setNtime] = useState('10:00');
  const [timeline, setTimeline] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stageMenu, setStageMenu] = useState(false);
  const [stageSel, setStageSel] = useState(null);
  const [reasonStep, setReasonStep] = useState(false);
  const [stageReason, setStageReason] = useState('');
  const [flash, setFlash] = useState('');
  const [fwdOpen, setFwdOpen] = useState(false);   // forward-to-another-rep picker
  const [assignees, setAssignees] = useState([]);
  const [fwdTo, setFwdTo] = useState('');
  const [fwdNote, setFwdNote] = useState('');
  const flashTimer = useRef();
  const toast = (m) => { setFlash(m); clearTimeout(flashTimer.current); flashTimer.current = setTimeout(() => setFlash(''), 2000); };
  const me = crmUser();

  const load = () => {
    crm.get(`/leads/${id}`).then(({ data }) => { setLead(data.lead); setEdit({ ...data.lead }); });
    crm.get(`/leads/${id}/timeline`).then(({ data }) => setTimeline(data.timeline || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { crm.get('/campaigns').then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {}); }, []);

  if (!lead) return (
    <Shell onClose={onClose}><div className="p-8 text-center text-[var(--muted)]">Loading…</div></Shell>
  );

  // `note` is stored on the stage-history row, so a backward move keeps its reason.
  const setStatus = async (status, note) => {
    await crm.put(`/leads/${id}/status`, { status, note: note || undefined });
    await load(); onChanged && onChanged(); toast(`Moved to ${stageMeta(status).label}`);
    // A stage change is a completed action — return to the leads list, like Save.
    if (onClose) setTimeout(onClose, 700);
  };
  const setPriority = async (priority) => {
    await crm.put(`/leads/${id}`, { priority });
    await load(); onChanged && onChanged(); toast(`Priority set to ${priority}`);
  };
  const assignCampaign = async (campaignId) => {
    await crm.put(`/leads/${id}`, { campaignId: campaignId || null });
    await load(); onChanged && onChanged(); toast(campaignId ? 'Campaign assigned' : 'Set to Direct');
  };
  const saveInfo = async () => {
    setSaving(true);
    const { name, email, phone, company, city, interest, discussion, message, value, expectedClose } = edit;
    await crm.put(`/leads/${id}`, { name, email, phone, company, city, interest, discussion, message, value: Number(value) || 0, expectedClose: expectedClose || null });
    setSaving(false); await load(); onChanged && onChanged(); toast('Changes saved ✓');
    // Return to the leads list after saving. Brief delay so the toast is seen first.
    if (onClose) setTimeout(onClose, 700);
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await crm.post(`/leads/${id}/notes`, { body: note }); setNote(''); await load(); toast('Note added');
  };
  const addTask = async () => {
    if (!nt.trim()) return;
    let calendarUrl = '';
    if (isMeeting(ntype) && nd) {
      calendarUrl = buildCalendarUrl({ title: nt, date: nd, time: ntime, details: `Lead: ${lead.name}${lead.company ? ' · ' + lead.company : ''}`, guests: [lead.email] });
    }
    await crm.post('/tasks', { title: nt, type: ntype, dueDate: nd || null, meetingTime: isMeeting(ntype) ? ntime : null, calendarUrl: calendarUrl || null, leadId: id });
    if (calendarUrl) window.open(calendarUrl, '_blank'); // open Google Calendar to create the event + Meet
    setNt(''); setNd(''); await load(); onChanged && onChanged();
    toast(calendarUrl ? 'Follow-up added — opening Google Calendar' : 'Follow-up added');
  };
  const toggleTask = async (t) => { await crm.put(`/tasks/${t.id}`, { done: !t.done }); await load(); onChanged && onChanged(); toast(t.done ? 'Reopened' : 'Completed ✓'); };

  // Forward: hand this lead to another rep (e.g. it belongs to their area). The
  // list of people to forward to is fetched lazily the first time the picker opens.
  const openForward = async () => {
    setFwdOpen(true); setFwdTo(''); setFwdNote('');
    if (!assignees.length) {
      try { const { data } = await crm.get('/leads/meta/assignees'); setAssignees(data.assignees || []); } catch { /* keep empty */ }
    }
  };
  const doForward = async () => {
    if (!fwdTo) return;
    try {
      const { data } = await crm.post(`/leads/${id}/forward`, { to_owner_id: fwdTo, note: fwdNote.trim() || undefined });
      setFwdOpen(false);
      onChanged && onChanged();
      toast(`Forwarded to ${data.owner_name || 'rep'}`);
      onClose && onClose(); // it may have left this rep's scope — return to the list
    } catch (e) { toast(e.response?.data?.error || 'Could not forward'); }
  };

  const notes = lead.LeadNotes || [];
  const history = lead.LeadStatusHistories || [];
  const visitor = lead.Visitor;

  // guided sales-path logic
  const cur = lead.status;
  const closed = cur === 'won' || cur === 'lost';
  const curIdx = PATH.indexOf(cur);
  const nextStage = curIdx >= 0 && curIdx < PATH.length - 1 ? PATH[curIdx + 1] : null;
  const prevStage = curIdx > 0 ? PATH[curIdx - 1] : null;
  // Forward jumps are unrestricted. Moving BACK along the path needs a reason,
  // which is written to the stage history so the walk-back is auditable.
  const isBackward = (target) => {
    const ti = PATH.indexOf(target);
    return ti >= 0 && curIdx >= 0 && ti < curIdx;
  };
  const stageOptions = closed
    ? PATH.filter((k) => k !== 'won').map((k) => ({ key: k, note: 'reopen here', enabled: true }))
    : [
        ...PATH.slice(0, curIdx).map((k) => ({ key: k, note: 'move back · needs reason', enabled: true })),
        ...PATH.slice(curIdx + 1).filter((k) => k !== 'won').map((k, i) => ({ key: k, note: i === 0 ? 'next step' : 'jump ahead', enabled: true })),
        ...(cur !== 'won' ? [{ key: 'won', note: 'close · won', enabled: true }] : []),
        { key: 'lost', note: 'close · lost', enabled: true },
      ];
  const closeStageMenu = () => { setStageMenu(false); setReasonStep(false); setStageReason(''); };
  const openStageMenu = () => { setStageSel(closed ? 'qualified' : (nextStage || prevStage || 'won')); setReasonStep(false); setStageReason(''); setStageMenu(true); };

  return (
    <Shell>
      {flash && <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[70] text-white text-sm font-bold px-5 py-2.5 rounded-xl shadow-xl flex items-center gap-2" style={{ background: '#0b8a4f' }}>{flash}</div>}
      <div className="sticky top-0 z-20 flex items-center justify-between gap-4 px-6 py-4 border-b border-[var(--border)] bg-[var(--card)] rounded-t-2xl">
        <button onClick={onClose} className="flex items-center gap-1.5 text-sm font-bold text-[var(--acc2)] hover:gap-2.5 transition-all shrink-0">← Back to leads</button>
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={openForward} title="Forward this lead to another rep"
            className="shrink-0 border border-[var(--border)] text-[var(--text)] text-[13px] font-bold px-3 py-2 rounded-lg hover:bg-[var(--soft)]">⇄ Forward</button>
          <div className="text-right min-w-0">
            <div className="text-[16px] font-extrabold text-[var(--text)] leading-tight truncate">{lead.name || '(no name)'}</div>
            <div className="text-[12px] text-[var(--muted)] truncate">{lead.company || '—'} · owner: {lead.owner?.name || 'unassigned'}</div>
          </div>
        </div>
      </div>

      {/* Forward-to-rep picker */}
      {fwdOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 70 }} onClick={() => setFwdOpen(false)}>
          <div style={{ width: '100%', maxWidth: 380, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 20 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', marginBottom: 12 }}>Forward lead</div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: .6, color: 'var(--muted)', fontWeight: 700 }}>Forward to</label>
            <select value={fwdTo} onChange={(e) => setFwdTo(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginTop: 5, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 14 }}>
              <option value="">Select a rep…</option>
              {assignees.filter((a) => a.id !== lead.owner_id).map((a) => (
                <option key={a.id} value={a.id}>{a.name}{a.role === 'admin' ? ' (admin)' : ''}</option>
              ))}
            </select>
            <div style={{ height: 12 }} />
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: .6, color: 'var(--muted)', fontWeight: 700 }}>Note (optional)</label>
            <input value={fwdNote} onChange={(e) => setFwdNote(e.target.value)} placeholder="Why you're forwarding"
              style={{ width: '100%', padding: '10px 12px', marginTop: 5, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 14 }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button onClick={() => setFwdOpen(false)} style={{ flex: 1, padding: 11, borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={doForward} disabled={!fwdTo} style={{ flex: 1, padding: 11, borderRadius: 10, border: 'none', background: '#0b6e3e', color: '#fff', fontWeight: 700, cursor: fwdTo ? 'pointer' : 'default', opacity: fwdTo ? 1 : 0.5 }}>Forward</button>
            </div>
          </div>
        </div>
      )}

      {/* priority */}
      <div className="px-6 mt-4 flex items-center gap-2">
        <span className="text-[11px] uppercase font-bold text-[var(--muted)] tracking-wide mr-1">Priority</span>
        {PRIORITIES.map((p) => (
          <button key={p.key} onClick={() => setPriority(p.key)}
            className="text-xs font-bold px-3 py-1 rounded-full border transition-colors"
            style={lead.priority === p.key ? { background: p.color, borderColor: p.color, color: '#fff' } : { borderColor: 'var(--border)', color: 'var(--muted)' }}>
            {p.dot}{p.label}
          </button>
        ))}
      </div>

      {/* stage — guided sales path */}
      <div className="px-6 mt-3">
        <div className="lp-path">
          {PATH.map((k, i) => {
            const s = stageMeta(k); const done = curIdx >= 0 && i < curIdx; const isCur = k === cur;
            return (
              <div key={k} className={done || isCur ? 'lp-seg' : 'lp-seg lp-up'} title={s.label}
                style={isCur ? { background: s.color } : done ? { background: 'var(--acc)' } : undefined}>
                {done ? '✓ ' : isCur ? '● ' : ''}{s.label}
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          {!closed && nextStage && <button onClick={() => setStatus(nextStage)} className="acc-bg text-white text-[13px] font-bold px-3.5 py-2 rounded-lg">Advance to {stageMeta(nextStage).label} →</button>}
          {!closed && cur !== 'won' && nextStage !== 'won' && <button onClick={() => setStatus('won')} className="text-white text-[13px] font-bold px-3.5 py-2 rounded-lg" style={{ background: stageMeta('won').color }}>🏆 Mark Won</button>}
          {closed && <button onClick={openStageMenu} className="border border-[var(--border)] text-[var(--muted)] text-[13px] font-bold px-3.5 py-2 rounded-lg">↺ Reopen</button>}
          <button onClick={() => (stageMenu ? setStageMenu(false) : openStageMenu())} className="border border-[var(--border)] text-[var(--text)] text-[13px] font-bold px-3.5 py-2 rounded-lg">Change stage ▾</button>
          {!closed && <button onClick={() => setStatus('lost')} className="text-[13px] font-semibold px-2 py-2 rounded-lg" style={{ color: stageMeta('lost').color }}>✕ Mark Lost</button>}
          <span className="text-[12px] text-[var(--muted)] ml-auto">Current: <b className="text-[var(--text)]">{stageMeta(cur).label}</b></span>
        </div>

        {stageMenu && (
          <div className="mt-3 w-full max-w-sm rounded-xl border border-[var(--border)] bg-[var(--card)] card-lux overflow-hidden">
            <div className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] border-b border-[var(--border)] bg-[var(--soft)]">
              {reasonStep
                ? `Why move back to ${stageMeta(stageSel).label}?`
                : closed ? 'Reopen to — pick one, then Confirm' : 'Change stage — pick one, then Confirm'}
            </div>
            {reasonStep ? (
              <div className="p-4">
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {BACK_REASONS.map((r) => (
                    <button key={r} onClick={() => setStageReason(r)}
                      className="text-[12px] font-semibold px-2.5 py-1.5 rounded-lg border transition-colors"
                      style={stageReason === r
                        ? { background: 'var(--acc-soft)', borderColor: 'var(--acc)', color: 'var(--acc2)' }
                        : { borderColor: 'var(--border)', color: 'var(--muted)' }}>{r}</button>
                  ))}
                </div>
                <textarea value={stageReason} onChange={(e) => setStageReason(e.target.value)} rows={2}
                  placeholder="…or type your own reason"
                  className="w-full text-[13px] rounded-lg p-2.5 outline-none border border-[var(--border)] bg-[var(--soft)] text-[var(--text)]" />
                <div className="text-[11px] text-[var(--muted)] mt-1.5">Saved to the lead's stage history.</div>
              </div>
            ) : (
            <div className="max-h-60 overflow-auto">
              {stageOptions.map((o) => {
                const s = stageMeta(o.key); const sel = stageSel === o.key && o.enabled;
                return (
                  <button key={o.key} disabled={!o.enabled} onClick={() => setStageSel(o.key)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 border-b border-[var(--border)] text-left ${o.enabled ? 'hover:bg-[var(--soft)]' : 'opacity-50 cursor-default'}`}
                    style={sel ? { background: s.color + '22', boxShadow: `inset 3px 0 0 ${s.color}` } : undefined}>
                    <span className="w-3 h-3 rounded-full shrink-0 flex items-center justify-center" style={{ background: o.enabled ? s.color : 'var(--muted)' }}>
                      {sel && <span className="text-white text-[9px] font-black leading-none">✓</span>}
                    </span>
                    <span className="text-sm font-bold" style={{ color: sel ? s.color : (o.enabled ? 'var(--text)' : 'var(--muted)') }}>{s.label}</span>
                    <span className="ml-auto text-[11px] font-semibold" style={{ color: sel ? s.color : 'var(--muted)' }}>{sel ? 'selected' : (o.enabled ? o.note : '🔒 ' + o.note)}</span>
                  </button>
                );
              })}
            </div>
            )}
            <div className="flex justify-end gap-2 px-4 py-2.5 border-t border-[var(--border)]">
              <button onClick={() => (reasonStep ? setReasonStep(false) : closeStageMenu())} className="text-sm font-semibold text-[var(--muted)] px-3 py-1.5 rounded-lg border border-[var(--border)]">
                {reasonStep ? '← Back' : 'Cancel'}
              </button>
              {reasonStep ? (
                <button onClick={() => { setStatus(stageSel, stageReason.trim()); closeStageMenu(); }} disabled={!stageReason.trim()}
                  className="acc-bg text-white text-sm font-bold px-3.5 py-1.5 rounded-lg disabled:opacity-50">Move back</button>
              ) : (
                <button onClick={() => { if (!stageSel) return; if (isBackward(stageSel)) { setReasonStep(true); return; } setStatus(stageSel); closeStageMenu(); }}
                  disabled={!stageSel} className="acc-bg text-white text-sm font-bold px-3.5 py-1.5 rounded-lg disabled:opacity-50">Confirm change</button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-6">
        {/* editable info */}
        <div className="space-y-2.5">
          <h3 className="text-sm font-bold text-[var(--text)]">Lead info</h3>
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold">Campaign</label>
            <select value={lead.Campaign?.id || ''} onChange={(e) => assignCampaign(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm bg-[var(--card)] text-[var(--text)] focus:ring-2 focus:ring-teal-500 focus:border-transparent">
              <option value="">Direct (no campaign)</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {['name', 'email', 'phone', 'company', 'city', 'interest'].map((f) => (
            <div key={f}>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold">{f}</label>
              <input value={edit[f] || ''} onChange={(e) => setEdit({ ...edit, [f]: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold">Deal value (₹)</label>
              <input type="number" value={edit.value || ''} onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                placeholder="0" className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
            </div>
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold">Expected close</label>
              <input type="date" value={edit.expectedClose || ''} onChange={(e) => setEdit({ ...edit, expectedClose: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
            </div>
          </div>
          {Array.isArray(lead.products) && lead.products.length > 0 && (
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold">Interested products</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {lead.products.map((p) => (
                  <span key={p} className="text-[12px] font-semibold px-2.5 py-1 rounded-full bg-teal-500/10 text-teal-700 border border-teal-500/30">{p}</span>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold">Discussion details</label>
            <textarea value={edit.discussion || ''} onChange={(e) => setEdit({ ...edit, discussion: e.target.value })}
              placeholder="What the customer discussed…" rows={3}
              className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
          </div>
          <textarea value={edit.message || ''} onChange={(e) => setEdit({ ...edit, message: e.target.value })}
            placeholder="Message" rows={2}
            className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
          <button onClick={saveInfo} disabled={saving}
            className="bg-[#1a8046] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#156637] disabled:opacity-60">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <div className="text-[11px] text-[var(--muted)] pt-1">Source: {lead.source}
            {visitor && <> · Visitor: {visitor.device}/{visitor.browser}/{visitor.os} · visits {visitor.visitCount}</>}</div>
          <div className="text-[11px] pt-0.5">
            {lead.consent
              ? <span className="text-emerald-600">✓ Consented to contact{lead.consentAt ? ` · ${new Date(lead.consentAt).toLocaleString()}` : ''}</span>
              : <span className="text-[var(--muted)]">Consent: not recorded</span>}
          </div>
        </div>

        {/* notes + history */}
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-bold text-[var(--text)] mb-2">Notes</h3>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…"
                onKeyDown={(e) => e.key === 'Enter' && addNote()}
                className="flex-1 px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
              <button onClick={addNote} className="bg-gray-800 text-white text-sm px-3 rounded-lg">Add</button>
            </div>
            <div className="mt-2 space-y-2 max-h-40 overflow-auto">
              {notes.length === 0 && <div className="text-xs text-[var(--muted)]">No notes yet.</div>}
              {notes.map((n) => (
                <div key={n.id} className="bg-[var(--soft)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm">
                  {n.body}<div className="text-[10px] text-[var(--muted)] mt-0.5">{n.author?.name} · {new Date(n.createdAt).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-bold text-[var(--text)] mb-2">Status history</h3>
            <div className="space-y-1.5 max-h-32 overflow-auto">
              {history.length === 0 && <div className="text-xs text-[var(--muted)]">No changes yet.</div>}
              {history.map((h) => (
                <div key={h.id} className="text-xs text-[var(--muted)]">
                  <b>{h.fromStatus || '—'}</b> → <b>{h.toStatus}</b> · {h.changedBy?.name} · {new Date(h.createdAt).toLocaleDateString()}
                  {h.note && <span className="text-[var(--muted)]"> · {h.note}</span>}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-[var(--text)] mb-2">Follow-ups</h3>
            <div className="flex gap-2 flex-wrap">
              <select value={ntype} onChange={(e) => setNtype(e.target.value)} className="px-2 py-2 border border-[var(--border)] rounded-lg text-sm bg-[var(--card)] text-[var(--text)]">
                {TASK_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <input value={nt} onChange={(e) => setNt(e.target.value)} placeholder="Add a follow-up…"
                onKeyDown={(e) => e.key === 'Enter' && addTask()}
                className="flex-1 min-w-[140px] px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
              <input type="date" value={nd} onChange={(e) => setNd(e.target.value)} className="px-2 py-2 border border-[var(--border)] rounded-lg text-sm" />
              {isMeeting(ntype) && <input type="time" value={ntime} onChange={(e) => setNtime(e.target.value)} className="px-2 py-2 border border-[var(--border)] rounded-lg text-sm" title="Meeting time" />}
              <button onClick={addTask} className="acc-bg text-white text-sm px-3 rounded-lg font-semibold">Add</button>
            </div>
            {isMeeting(ntype) && <div className="text-[11px] text-[var(--muted)] mt-1">📅 Adds a Google Calendar event (Meet link added on Save) for <b>{GCAL_ACCOUNT}</b>.</div>}
            <div className="mt-2 space-y-1.5 max-h-32 overflow-auto">
              {(lead.Tasks || []).length === 0 && <div className="text-xs text-[var(--muted)]">No follow-ups scheduled.</div>}
              {(lead.Tasks || []).map((t) => (
                <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={t.done} onChange={() => toggleTask(t)} />
                  {t.type && t.type !== 'todo' && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-[var(--soft)] text-[var(--muted)]">{t.type}</span>}
                  <span className={t.done ? 'line-through text-[var(--muted)]' : 'text-[var(--text)]'}>{t.title}</span>
                  <span className="ml-auto flex items-center gap-2 shrink-0">
                    {t.calendarUrl && <a href={t.calendarUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[11px] font-bold text-[var(--acc2)] hover:underline" title="Add to Google Calendar">📅 Calendar</a>}
                    {t.dueDate && <span className="text-[11px] text-[var(--muted)]">{t.dueDate}{t.meetingTime ? ' · ' + t.meetingTime : ''}</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Digital Engagement Timeline */}
      <div className="px-6 pb-6">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-bold text-[var(--text)]">Digital Engagement Timeline</h3>
          <span className="text-[11px] text-[var(--muted)]">every card / microsite touch, in order</span>
        </div>
        {timeline.length === 0 ? (
          <div className="text-xs text-[var(--muted)]">No engagement recorded yet.</div>
        ) : (
          <div className="relative pl-1 max-h-72 overflow-auto">
            <div className="absolute left-[10px] top-2 bottom-2 w-px bg-[var(--border)]" />
            {timeline.map((t, i) => (
              <div key={i} className="relative flex items-start gap-3 pb-3.5 pl-1">
                <div className="relative z-10 w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px] shrink-0 ring-2 ring-[var(--card)]"
                  style={{ background: (GC[t.group] || '#64748b') + '22' }}>
                  <span>{TICON[t.type] || '•'}</span>
                </div>
                <div className="min-w-0 -mt-0.5">
                  <div className="text-[13px] text-[var(--text)] font-medium leading-snug">{t.title}</div>
                  <div className="text-[11px] text-[var(--muted)]">
                    <span className="font-semibold uppercase tracking-wide" style={{ color: GC[t.group] || '#64748b' }}>{t.group}</span>
                    {t.meta ? <> · {t.meta}</> : ''} · {fmtWhen(t.at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="fixed inset-0 z-50 overflow-auto" style={{ background: 'var(--bg-b)' }}>
      <div className="max-w-5xl mx-auto px-3 sm:px-5 py-5">
        <div className="bg-[var(--card)] rounded-2xl border border-[var(--border)] card-lux">
          {children}
        </div>
      </div>
    </div>
  );
}
